import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useLocale, type MessageKey } from '../../i18n';
import {
  fetchOrganizationAudit,
  type OrganizationAuditEvent,
  type OrganizationAuditPage,
  type OrganizationAuditResult,
} from '../../lib/api/organizationAudit';

// ─────────────────────────────────────────────────────────────────────
// LOGG — the organization's own audit trail (#935, 2026-11-01).
//
// Who did what in this organization, newest first: the organization's slice
// of the audit trail, read by its access managers (Eier and Administrator by
// default). The server decides everything that matters — the organization,
// the rows left out (the people's own sign-ins), what of a row is shown, and
// that every read is itself logged — and this page draws the answer. A
// period and an area are the only choices; the action code is shown beside
// the area's name so a support question can quote it exactly.
//
// Every string is a catalog key.
// ─────────────────────────────────────────────────────────────────────

export interface OrganizationAuditViewProps {
  /** The organization the portal is acting in — for the heading only. */
  organizationName: string | null;
  onBack: () => void;
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** The periods offered, in days. The server's widest window is 366. */
const PERIODS = [7, 30, 90, 365] as const;

/** The areas offered, as action prefixes the server matches. */
const AREAS: readonly { prefix: string; label: MessageKey }[] = [
  { prefix: 'organization_role.', label: 'auditLog.area.roles' },
  { prefix: 'booking.', label: 'auditLog.area.bookings' },
  { prefix: 'activity.', label: 'auditLog.area.activities' },
  { prefix: 'member.', label: 'auditLog.area.members' },
  { prefix: 'court.', label: 'auditLog.area.courts' },
  { prefix: 'configuration.', label: 'auditLog.area.settings' },
  { prefix: 'payment.', label: 'auditLog.area.payments' },
  { prefix: 'impersonation.', label: 'auditLog.area.starpoint' },
];

/** The area a row belongs to, for its label; null when none matches. */
function areaOf(action: string): MessageKey | null {
  return AREAS.find((a) => action.startsWith(a.prefix))?.label ?? null;
}

export function OrganizationAuditView({ organizationName, onBack }: OrganizationAuditViewProps) {
  const { t, formatDate } = useLocale();
  const [days, setDays] = useState<number>(30);
  const [area, setArea] = useState<string>('');
  const [result, setResult] = useState<OrganizationAuditResult | null>(null);
  const [events, setEvents] = useState<OrganizationAuditEvent[]>([]);
  const [accounts, setAccounts] = useState<OrganizationAuditPage['accounts']>({});
  const [loadingMore, setLoadingMore] = useState(false);

  const filters = useCallback(
    () => ({
      from: new Date(Date.now() - days * DAY_MS).toISOString(),
      ...(area ? { action: area } : {}),
    }),
    [days, area],
  );

  useEffect(() => {
    let live = true;
    setResult(null);
    void fetchOrganizationAudit(filters()).then((r) => {
      if (!live) return;
      setResult(r);
      setEvents(r.state === 'ok' ? r.page.events : []);
      setAccounts(r.state === 'ok' ? r.page.accounts : {});
    });
    return () => {
      live = false;
    };
  }, [filters]);

  const nextCursor = result?.state === 'ok' ? result.page.nextCursor : null;
  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    const more = await fetchOrganizationAudit(filters(), nextCursor);
    setLoadingMore(false);
    if (more.state !== 'ok') return;
    setEvents((prev) => [...prev, ...more.page.events]);
    setAccounts((prev) => ({ ...prev, ...more.page.accounts }));
    setResult(more);
  };

  const nameOf = (id: string | null): string | null =>
    id ? (accounts[id.toLowerCase()]?.displayName ?? accounts[id]?.displayName ?? null) : null;

  const who = (event: OrganizationAuditEvent): string => {
    switch (event.actor.kind) {
      case 'person':
        return nameOf(event.actor.id) ?? t('auditLog.actor.unknownPerson');
      case 'member':
        return t('auditLog.actor.member');
      case 'starpoint':
        return t('auditLog.actor.starpoint');
      case 'device':
        return t('auditLog.actor.device');
      case 'system':
        return t('auditLog.actor.system');
      case 'provider':
        return t('auditLog.actor.provider');
    }
  };

  const subject = (event: OrganizationAuditEvent): string | null => {
    if (event.subjectType === 'user') return nameOf(event.subjectId) ?? t('auditLog.subject.user');
    return event.subjectType;
  };

  const body = () => {
    if (result === null) {
      return (
        <p className="text-sm text-ink-3" data-testid="audit-log-loading">
          {t('auditLog.loading')}
        </p>
      );
    }
    if (result.state === 'forbidden') {
      return (
        <p className="text-sm text-danger" data-testid="audit-log-forbidden">
          {t('auditLog.forbidden')}
        </p>
      );
    }
    if (result.state !== 'ok') {
      return (
        <p className="text-sm text-danger" data-testid="audit-log-unavailable">
          {t('auditLog.unavailable')}
        </p>
      );
    }
    if (events.length === 0) {
      return (
        <p className="text-sm text-ink-3" data-testid="audit-log-empty">
          {t('auditLog.empty')}
        </p>
      );
    }
    return (
      <div className="space-y-3">
        <ul
          className="divide-y divide-line border border-line rounded-lg"
          data-testid="audit-log-rows"
        >
          {events.map((event) => {
            const areaKey = areaOf(event.action);
            const about = subject(event);
            return (
              <li key={event.id} className="px-3 py-2 text-sm flex flex-col sm:flex-row sm:gap-4">
                <span className="text-ink-3 font-mono text-xs sm:w-36 flex-shrink-0">
                  {formatDate(new Date(event.at), { dateStyle: 'short', timeStyle: 'short' })}
                </span>
                <span className="sm:w-40 flex-shrink-0">{who(event)}</span>
                <span className="flex-1 min-w-0">
                  {areaKey && <span className="mr-2">{t(areaKey)}</span>}
                  <span className="font-mono text-xs text-ink-3 break-all">{event.action}</span>
                  {about && <span className="block text-xs text-ink-3">{about}</span>}
                </span>
              </li>
            );
          })}
        </ul>
        {nextCursor && (
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="btn-outline text-xs"
            data-testid="audit-log-more"
          >
            {loadingMore ? t('auditLog.loading') : t('auditLog.more')}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 slide-up" data-testid="audit-log-view">
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
        data-testid="audit-log-back"
      >
        <ArrowLeft size={12} /> {t('auditLog.back')}
      </button>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold mb-1">{t('auditLog.title')}</h1>
        <p className="text-sm text-ink-3">
          {t('auditLog.lead', {
            organization: organizationName ?? t('auditLog.thisOrganization'),
          })}
        </p>
        <p className="text-xs text-ink-3 mt-1">{t('auditLog.notShown')}</p>
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        <label className="text-xs text-ink-3 flex flex-col gap-1">
          {t('auditLog.period')}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="input text-sm"
            data-testid="audit-log-period"
          >
            {PERIODS.map((d) => (
              <option key={d} value={d}>
                {t('auditLog.periodDays', { days: String(d) })}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-ink-3 flex flex-col gap-1">
          {t('auditLog.areaLabel')}
          <select
            value={area}
            onChange={(e) => setArea(e.target.value)}
            className="input text-sm"
            data-testid="audit-log-area"
          >
            <option value="">{t('auditLog.area.all')}</option>
            {AREAS.map((a) => (
              <option key={a.prefix} value={a.prefix}>
                {t(a.label)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {body()}
    </div>
  );
}
